import React, { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useLang } from '../i18n';
import api from '../api';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const ContactDialog = ({ open, onClose }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [company, setCompany] = useState(''); // honeypot — must stay empty
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const openedAtRef = React.useRef(Date.now());

  // reset the "form opened" timestamp whenever the dialog opens (bot time-trap)
  React.useEffect(() => {
    if (open) openedAtRef.current = Date.now();
  }, [open]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t('contact_err_name');
    if (!EMAIL_RE.test(form.email.trim())) errs.email = t('contact_err_email');
    if (!form.message.trim()) errs.message = t('contact_err_message');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const reset = () => {
    setForm({ name: '', email: '', subject: '', message: '' });
    setCompany('');
    setErrors({});
  };

  const handleClose = () => {
    if (sending) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.sendContact({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        company: company.trim(),
        elapsed_ms: Date.now() - openedAtRef.current,
      });
      toast.success(t('contact_success'));
      reset();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('contact_error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Mail className="h-5 w-5 text-primary" />
            {t('contact_title')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t('contact_desc')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Honeypot: hidden from real users; bots that fill it are silently ignored */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden" style={{ opacity: 0 }}>
            <label htmlFor="contact-company">Company (leave empty)</label>
            <input
              id="contact-company"
              name="company"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">{t('contact_name')}</Label>
              <Input
                id="contact-name"
                data-testid="contact-name-input"
                value={form.name}
                onChange={set('name')}
                placeholder={t('contact_name_ph')}
                aria-invalid={!!errors.name}
                aria-label={t('contact_name')}
              />
              {errors.name && (
                <p className="text-xs text-destructive" data-testid="contact-name-error">{errors.name}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">{t('contact_email')}</Label>
              <Input
                id="contact-email"
                type="email"
                data-testid="contact-email-input"
                value={form.email}
                onChange={set('email')}
                placeholder={t('contact_email_ph')}
                aria-invalid={!!errors.email}
                aria-label={t('contact_email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive" data-testid="contact-email-error">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-subject">
              {t('contact_subject')}{' '}
              <span className="text-xs font-normal text-muted-foreground">({t('contact_optional')})</span>
            </Label>
            <Input
              id="contact-subject"
              data-testid="contact-subject-input"
              value={form.subject}
              onChange={set('subject')}
              placeholder={t('contact_subject_ph')}
              aria-label={t('contact_subject')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-message">{t('contact_message')}</Label>
            <Textarea
              id="contact-message"
              data-testid="contact-message-input"
              value={form.message}
              onChange={set('message')}
              placeholder={t('contact_message_ph')}
              rows={5}
              aria-invalid={!!errors.message}
              aria-label={t('contact_message')}
            />
            {errors.message && (
              <p className="text-xs text-destructive" data-testid="contact-message-error">{errors.message}</p>
            )}
          </div>

          <p className="text-xs text-muted-foreground">{t('contact_privacy_note')}</p>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={sending}
              data-testid="contact-cancel-btn"
            >
              {t('close')}
            </Button>
            <Button type="submit" disabled={sending} data-testid="contact-submit-btn" className="gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? t('contact_sending') : t('contact_send')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
